import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { LogOut, User, Mail, Shield, CheckCircle, Bell, ChevronDown, Camera, Lock } from 'lucide-react';
import Button from '../components/common/Button/Button';
import UpdateProfilePhoto from '../components/UpdateProfilePhoto';
import ChangePassword from '../components/ChangePassword';
import './Dashboard.css';
import './Services.css';
import Logo from "../assets/hyderotrace.svg";
import img1 from "../assets/img-1.png";
import noProfile from "../assets/noprofile.png";
import groundWater from "../assets/videos/ground-water.mp4";
import naturalRainfall from "../assets/videos/natural-rainfall.mp4";
import rainEnhancement from "../assets/videos/rain-enhancement.mp4";
import surfaceWater from "../assets/videos/surface-water.mp4";
import unifiedIntelligence from "../assets/videos/unified-intelligence.mp4";
import climatology from "../assets/videos/climatology.mp4";

export const Dashboard = ({ setView }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [hasCheckedSession] = useState(true); // Prevent re-checks
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUpdatePhotoOpen, setIsUpdatePhotoOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [profilePictureTimestamp, setProfilePictureTimestamp] = useState(Date.now());
  const profileRef = useRef(null);

  // Function to fetch user details from API
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        console.warn('No valid session found');
        return null;
      }

      // Fetch user details from API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      console.log('User Details API Response:', data);

      if (data.status && data.data?.user) {
        const userData = data.data.user;
        console.log('Dashboard - User Data:', userData);

        // Normalize profile picture field - ensure 'picture' is set
        if (userData.profile_picture && !userData.picture) {
          userData.picture = userData.profile_picture;
        }

        setUser(userData);
        // Also store in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } else {
        throw new Error('Invalid user data response');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // If API fails, try to use stored user data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        return userData;
      }
      return null;
    }
  };

  // Load user data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      const userData = await fetchUserDetails();
      if (!userData) {
        setAlert({
          type: 'error',
          message: 'Error loading user information'
        });
        setTimeout(() => {
          setView('signin');
        }, 500);
      }
      setLoading(false);
    };

    loadInitialData();
  }, [setView, hasCheckedSession]);

  // Click outside to close profile dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileRef]);

  const handleLogout = () => {
    try {
      console.log('=== LOGOUT ===');

      // Clear all authentication data from localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberedEmail');

      console.log('Tokens and user data cleared from localStorage');

      setAlert({
        type: 'success',
        message: 'Logged out successfully!'
      });

      // Redirect to login after a short delay
      setTimeout(() => {
        setView('signin');
      }, 1000);
    } catch (error) {
      console.error('Logout error:', error);
      setAlert({
        type: 'error',
        message: 'Error during logout. Please try again.'
      });
    }
  };

  const services = [
    {
      title: 'Ground Water',
      description: 'Subsurface Flow & Aquifer Status',
      videoUrl: groundWater,
      thumbnail: ''
    },
    {
      title: 'Surface Water',
      description: 'Reservoir Levels & River Monitoring',
      videoUrl: surfaceWater,
      thumbnail: ''
    },

    {
      title: 'Natural Rainfall',
      description: 'Real-time Precipitation',
      videoUrl: naturalRainfall,
      thumbnail: ''
    },

    {
      title: 'Rain Enhancement',
      description: 'Cloud Intelligence & Cloud Seeding',
      videoUrl: rainEnhancement,
      thumbnail: ''
    },
    {
      title: 'Climatology ',
      description: 'Extreme Weather Forecast',
      videoUrl: climatology,
      thumbnail: ''
    },
    {
      title: 'Unified Intelligence',
      description: 'AI-driven Analytics & Predictive Modeling',
      videoUrl: unifiedIntelligence,
      thumbnail: ''
    }
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',

        }}
      >
        <div style={{ color: '#888', fontSize: '16px' }}>Loading dashboard...</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='bg-[#000]' // Ensure black background
      style={{
        minHeight: '100vh',
        background: '#000814', // Very dark blue/black
        padding: '20px',
        color: '#fff',

      }}
    >
      {/* Header - Dashboard*/}
      <div className="inner-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        padding: '16px 14px',

      }}>
        {/* Logo Section */}
        <div>
          <img src={Logo} alt="Logo" className='innerlogo' style={{ height: '40px' }} />
        </div>

        {/* Right Side Controls */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

          {/* State Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#021020',
            border: '1px solid #1e293b',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            <span style={{ marginRight: '6px', color: '#64748b' }}>State:</span>
            <span style={{ color: '#e2e8f0', fontWeight: '500' }}>
              {user?.state || ''}
            </span>
          </div>

          {/* Region Info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#021020',
            border: '1px solid #1e293b',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            color: '#94a3b8'
          }}>
            <span style={{ marginRight: '6px', color: '#64748b' }}>Region:</span>
            <span style={{ color: '#e2e8f0', fontWeight: '500' }}>
              {user?.city || ''}
            </span>
          </div>

          {/* Notification Bell */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '38px',
            height: '38px',
            background: '#021020',
            border: '1px solid #1e293b',
            borderRadius: '6px',
            cursor: 'pointer',
            color: '#94a3b8',
            transition: 'all 0.2s'
          }}>
            <Bell size={18} />
          </div>

          {/* Profile Dropdown */}
          <div
            ref={profileRef}
            style={{ position: 'relative' }}
          >
            <div
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: '#021020',
                border: '1px solid #1e293b',
                borderRadius: '6px',
                padding: '4px 12px 4px 6px',
                cursor: 'pointer',
                gap: '10px',
                minWidth: '200px'
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '1px solid #3b82f6'
              }}>
                {/* Placeholder for user image if not available */}
                <img
                  key={`${user?.picture || user?.profile_picture}-${profilePictureTimestamp}`}
                  src={
                    user?.picture || user?.profile_picture
                      ? `${user?.picture || user?.profile_picture}?t=${profilePictureTimestamp}`
                      : noProfile
                  }
                  alt="User"
                  crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    const imageUrl = user?.picture || user?.profile_picture;
                    console.error('Failed to load profile picture:', imageUrl);
                    console.log('Attempting to load without cache-busting...');

                    // Try loading without timestamp first
                    if (e.target.src.includes('?t=')) {
                      e.target.src = imageUrl;
                    } else {
                      // Fallback to placeholder
                      e.target.src = noProfile;
                    }
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#fff', lineHeight: '1.2' }}>
                  {user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : user?.firstname || 'No Name'}
                </span>
              </div>

              <div style={{

                color: '#fff',
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '12px',
                fontWeight: '500',

                border: '1px solid #013762',
                background: 'rgba(0, 36, 65, 0.25)',
                boxShadow: '0 2px 2px 0 rgba(255, 255, 255, 0.25) inset',





              }}>
                {user?.role || 'No Role'}
              </div>

              <ChevronDown size={14} style={{ color: '#64748b' }} />
            </div>

            {/* Dropdown Menu */}
            {isProfileOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  width: '100%',
                  background: '#111827',
                  border: '1px solid #1f2937',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  zIndex: 50,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div style={{ padding: '4px' }}>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#e2e8f0',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      textAlign: 'left',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => e.target.closest('button').style.background = '#1f2937'}
                    onMouseOut={(e) => e.target.closest('button').style.background = 'transparent'}
                    onClick={() => {
                      setIsProfileOpen(false);
                      setIsUpdatePhotoOpen(true);
                    }}
                  >
                    <Camera size={16} />
                    Update Profile Photo
                  </button>

                  <div style={{ height: '1px', background: '#374151', margin: '4px 0' }}></div>

                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#e2e8f0',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      textAlign: 'left',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => e.target.closest('button').style.background = '#1f2937'}
                    onMouseOut={(e) => e.target.closest('button').style.background = 'transparent'}
                    onClick={() => {
                      setIsProfileOpen(false);
                      setIsChangePasswordOpen(true);
                    }}
                  >
                    <Lock size={16} />
                    Change Password
                  </button>

                  <div style={{ height: '1px', background: '#374151', margin: '4px 0' }}></div>

                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#ef4444',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      textAlign: 'left',
                      gap: '10px'
                    }}
                    onMouseOver={(e) => e.target.closest('button').style.background = '#FEF2F20D'} // slightly red
                    onMouseOut={(e) => e.target.closest('button').style.background = 'transparent'}
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{
            background: alert.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
            border: `1px solid ${alert.type === 'error' ? '#ef4444' : '#22c55e'}`,
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: alert.type === 'error' ? '#ef4444' : '#22c55e',
            fontSize: '14px'
          }}
        >
          <CheckCircle size={18} />
          <span style={{ flex: 1 }}>{alert.message}</span>
          <button
            onClick={() => setAlert(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </motion.div>
      )}


      {/* Services Grid */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '24px',
          textAlign: 'left',
          color: '#e2e8f0'
        }}>
          Modules
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '24px'
        }}>
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className='modules-box-bg'

              whileHover={{ y: -8 }}
            >
              <div>
                <div className='modules-heading-bg'>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    margin: '0 0 16px 0',
                    color: '#fff'
                  }}>
                    {service.title}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#94a3b8',
                    lineHeight: '1.6',
                    margin: '0'
                  }}>
                    {service.description}
                  </p>
                </div>

                <div style={{
                  width: '100%',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#000'
                }}>
                  <video
                    autoPlay
                    loop
                    muted

                    poster={service.thumbnail}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxHeight: '250px',
                      objectFit: 'cover'
                    }}
                    preload="metadata"
                  >
                    <source src={service.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>


              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Footer - Dashboard Style */}
      <div style={{
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid #333',
        textAlign: 'center',
        color: '#666',
        fontSize: '12px'
      }}>
        <p>© 2026 Accel1 Inc. All rights reserved     |     www.accel1.com</p>

      </div>

      {/* Update Profile Photo Modal */}
      <UpdateProfilePhoto
        isOpen={isUpdatePhotoOpen}
        onClose={() => setIsUpdatePhotoOpen(false)}
        onSuccess={async (newProfilePictureUrl) => {
          console.log('Profile photo upload successful, URL:', newProfilePictureUrl);

          // Update timestamp to force image reload (cache-busting)
          setProfilePictureTimestamp(Date.now());

          // Refetch user data from server to get the confirmed profile picture URL
          setTimeout(async () => {
            console.log('Refetching user data from server...');
            await fetchUserDetails();
            // Update timestamp again after refetch
            setProfilePictureTimestamp(Date.now());
          }, 500);

          // Show success alert
          setAlert({
            type: 'success',
            message: 'Profile picture updated successfully!'
          });

          // Auto-hide alert after 3 seconds
          setTimeout(() => setAlert(null), 3000);
        }}
      />

      {/* Change Password Modal */}
      <ChangePassword
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
        onSuccess={() => {
          // Show success alert
          setAlert({
            type: 'success',
            message: 'Password changed successfully!'
          });

          // Auto-hide alert after 3 seconds
          setTimeout(() => setAlert(null), 3000);
        }}
      />
    </motion.div>
  );
};

export default Dashboard;

